import React, { MutableRefObject, useContext, useEffect, useState } from 'react';
import { Channel, MessageInput, Window } from 'stream-chat-react';
import { useAtom } from 'jotai';
import ChannelHeader from '../ChannelHeader';
import ThreadStart from '../ThreadStart';
import MessageNotification from '../MessageNotification';
import CustomMessageInput from '../CustomMessageInput';
import { IChatChannel } from '../../interfaces/IChatChannel';
import { useChatClient, useUserBanListener } from '@bizzabo/chat-core';
import { getMemberFromUserOnChannel } from '../../utils/userUtils';
import { UserRole } from '../../types/UserRole';
import useTypeTracking, { RingTracking } from '../../analytics';
import SessionChatCtx from '../../context/SessionChatCtx';
import DeleteMessageDialog from '../Dialogs/DeleteMessageDialog';
import BanUserDialog from '../Dialogs/BanUserDialog';
import {
    messageIdToDeleteAtom,
    userBanForFirstTimeAtom,
    userIdToBanMessageAtom,
} from '../../atoms/MessageActionsAtoms';
import BanUserOneTimeDialogMessage from '../Dialogs/BanUserOneTimeDialogMessage';
import { UserBanStatus } from '../../types/UserBanStatus';
import ChatContent from '../ChatContent';
import PinnedMessages from '../PinnedMessages';
import MessageListWrapper from '../MessageListWrapper';
import BanUserInformationMessage from '../BanUserInformationMessage';
import ThreadWrapper from '../ThreadWrapper';

interface ISessionChat {
    chatChannel: IChatChannel;
    sessionChatContainer: MutableRefObject<HTMLElement>;
    chatUserRole?: UserRole;
}

const Index: React.FC<ISessionChat> = ({ chatChannel, sessionChatContainer, chatUserRole }) => {
    const { channel, title } = chatChannel;
    const sessionChatCtx = useContext(SessionChatCtx);

    const [messageIdToDelete] = useAtom(messageIdToDeleteAtom);
    const [userIdToBanMessage] = useAtom(userIdToBanMessageAtom);
    const [userBanForFirstTime, setUserBanForFirstTime] = useAtom(userBanForFirstTimeAtom);

    const [userRole, setUserRole] = useState<UserRole>(chatUserRole);
    const [isShowingPinned, setIsShowingPinned] = useState(null);

    const { Track, trackClickEvent } = useTypeTracking({
        data: {
            description: {
                channel_title: title,
                channel_type: 'SESSION_CHAT',
                session_id: sessionChatCtx.sessionId,
                channel_id: channel.id,
                message_type: 'TEXT',
            },
        },
    });

    const { client } = useChatClient();
    const isMyUserBanned = useUserBanListener(client);

    const updateIfUserIsModerator = async (channel, currentUser) => {
        const response = await getMemberFromUserOnChannel(channel, currentUser);
        if (response.members.length !== 1) {
            setUserRole(UserRole.Empty);
        } else {
            const member = response.members[0];
            setUserRole(member.role === 'moderator' ? UserRole.Moderator : UserRole.Empty);
        }
    };

    useEffect(() => {
        if (!userRole) {
            updateIfUserIsModerator(channel, client.user);
        }
    }, []);

    const handleShowPinnedChange = isPinnedShowing => {
        setIsShowingPinned(isPinnedShowing);
    };

    useEffect(() => {
        if (isMyUserBanned && userBanForFirstTime === UserBanStatus.None) {
            setUserBanForFirstTime(UserBanStatus.FirstTimeBan);
        }
    }, [isMyUserBanned]);

    return (
        !!userRole && (
            <Track>
                <Channel
                    channel={channel}
                    ThreadHeader={() => (
                        <ChannelHeader
                            channelTitle={title}
                            onCloseThread={() => setIsShowingPinned(false)}
                        />
                    )}
                    ThreadStart={ThreadStart}
                    MessageNotification={props => (
                        <MessageNotification
                            {...props}
                            onClosePinView={() => setIsShowingPinned(false)}
                        />
                    )}
                >
                    <Window>
                        {/* Don't change structure => see Chat content Comment for explanation */}
                        {/* Window must be direct parent of ChatContent */}
                        <ChatContent>
                            <ChannelHeader channelTitle={title} />
                            <PinnedMessages
                                chatChannel={chatChannel}
                                userRole={userRole}
                                onShowingPinnedChange={handleShowPinnedChange}
                                showPinView={isShowingPinned}
                            />
                            <MessageListWrapper chatChannel={chatChannel} userRole={userRole} />
                            {isMyUserBanned && userBanForFirstTime === UserBanStatus.Banned && (
                                <BanUserInformationMessage event={sessionChatCtx.event} />
                            )}
                            {!isShowingPinned && <MessageInput Input={CustomMessageInput} />}
                        </ChatContent>
                    </Window>
                    <ThreadWrapper chatChannel={chatChannel} userRole={userRole} />
                    {!!messageIdToDelete && (
                        <DeleteMessageDialog
                            sessionChatContainer={sessionChatContainer}
                            trackClickEvent={trackClickEvent}
                        />
                    )}
                    {!!userIdToBanMessage && (
                        <BanUserDialog
                            sessionChatContainer={sessionChatContainer}
                            trackClickEvent={trackClickEvent}
                        />
                    )}
                    {userBanForFirstTime !== UserBanStatus.Banned && isMyUserBanned && (
                        <BanUserOneTimeDialogMessage
                            sessionChatContainer={sessionChatContainer}
                            event={sessionChatCtx.event}
                            onClick={() => setUserBanForFirstTime(UserBanStatus.Banned)}
                        />
                    )}
                </Channel>
            </Track>
        )
    );
};

// TODO once 'something analytics' package is created this must be deleted
const TempRingTrackingContext = props => {
    return (
        <RingTracking>
            <Index {...props} />
        </RingTracking>
    );
};

export default TempRingTrackingContext;
